import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { lcApiBase, lcHttpError, lcStationId } from './logiciel-caisse-api.helper';
import { LogicielCaisseAuthService } from './logiciel-caisse-auth.service';
import { AccesLogicielCaisseService } from './acces-logiciel-caisse.service';
import { UsersCoordinationService } from './users-coordination.service';

export interface LogicielCaisseEntrepotOption {
  id: string;
  label: string;
}

export interface EntrepotRow {
  id: number;
  libelle?: string | null;
  user_id?: number;
  station_id?: number;
  is_reassort?: boolean | number | null;
  is_globale?: boolean | number | null;
}

/** Ligne tableau « Gestion des entrepôts » (ERP). */
export interface EntrepotTableRow {
  id: string;
  'CODE ENTREPÔT': string;
  LIBELLÉ: string;
  VILLE: string;
  PAYS: string;
  /** Ordre réassort (1, 2, …) si défini en base ; sinon « — ». */
  'PRIORITÉ'?: string;
  is_reassort?: boolean;
  is_globale?: boolean;
}

export interface EntrepotFormPayload {
  libelle: string;
  ville: string;
  pays: string;
  code_entrepot: string | null;
  is_reassort: boolean;
  is_globale: boolean;
}

export interface EntrepotCaisseOption {
  id: number;
  numero_de_caisse: number;
  entrepot_id: number;
  entrepot_libelle?: string;
  uuid?: string | null;
  has_uuid?: boolean;
  label?: string;
}

/** Entrepôt renvoyé après création / mise à jour (API). */
export interface EntrepotApiRecord {
  id: number;
  libelle?: string | null;
  ville?: string | null;
  pays?: string | null;
  code_entrepot?: string | null;
  station_id?: number | null;
  user_id?: number | null;
  is_reassort?: boolean | number | null;
  is_globale?: boolean | number | null;
}

interface EntrepotMutationResponse {
  success: boolean;
  entrepot?: Record<string, unknown>;
  error?: string;
}

interface EntrepotsListResponse {
  success: boolean;
  entrepots?: EntrepotRow[];
  error?: string;
}

interface EntrepotsErpListResponse {
  success: boolean;
  items?: EntrepotTableRow[];
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class LogicielCaisseEntrepotService {
  private entrepotOptionsCache$: Observable<LogicielCaisseEntrepotOption[]> | null = null;

  constructor(
    private readonly http: HttpClient,
    private readonly authService: LogicielCaisseAuthService,
    private readonly accesService: AccesLogicielCaisseService,
    private readonly coordinationService: UsersCoordinationService
  ) {}

  /** GET /api/entrepots/entrepot/:station_id */
  getByStation(): Observable<EntrepotRow[]> {
    let stationId: number;
    try {
      stationId = lcStationId(this.authService);
    } catch (e) {
      return throwError(() => (e instanceof Error ? e : new Error(String(e))));
    }
    const url = `${lcApiBase(this.authService)}/api/entrepots/entrepot/${stationId}`;
    return this.http.get<EntrepotsListResponse>(url).pipe(
      map((res) => {
        if (!res?.success || !Array.isArray(res.entrepots)) {
          throw new Error(res?.error || 'Réponse entrepôts invalide');
        }
        return res.entrepots;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors du chargement des entrepôts.'))
      )
    );
  }

  /**
   * GET /api/entrepots/entrepot/all?user_id=&station_id=
   * Liste publique (sans JWT) — filtre strict user + station (backend-logiciel-caisse).
   */
  getByUserAndStation(userId: number, stationId: number): Observable<EntrepotRow[]> {
    const uid = Number(userId);
    const sid = Number(stationId);
    if (!Number.isFinite(uid) || uid <= 0 || !Number.isFinite(sid) || sid <= 0) {
      console.error('[LC Entrepot][getByUserAndStation] IDs invalides', { userId, stationId });
      return throwError(() => new Error('user_id et station_id (entiers positifs) sont requis'));
    }
    const url = `${lcApiBase(this.authService)}/api/entrepots/entrepot/all`;
    const params = { user_id: String(uid), station_id: String(sid) };
    console.log('[LC Entrepot][getByUserAndStation] GET', url, params);
    return this.http.get<EntrepotsListResponse>(url, { params }).pipe(
      map((res) => {
        console.log('[LC Entrepot][getByUserAndStation] réponse brute', {
          success: res?.success,
          count: Array.isArray(res?.entrepots) ? res.entrepots.length : null,
          entrepots: res?.entrepots,
          error: res?.error,
        });
        if (!res?.success || !Array.isArray(res.entrepots)) {
          throw new Error(res?.error || 'Réponse entrepôts invalide');
        }
        return res.entrepots;
      }),
      catchError((err: HttpErrorResponse) => {
        console.error('[LC Entrepot][getByUserAndStation] erreur HTTP', err?.status, err?.error || err);
        return throwError(() => lcHttpError(err, 'Erreur lors du chargement des entrepôts.'));
      })
    );
  }

  /**
   * Liste pour le ng-select document : backend-logiciel-caisse (user_id + station_id).
   * Format aligné sur bindLabel="nom".
   */
  listForDocumentSelectByUserAndStation(
    userId: number,
    stationId: number
  ): Observable<
    Array<{
      id: number;
      nom: string;
      libelle: string;
      ville: string;
      pays: string;
      adresse: string;
      code_entrepot: string | null;
    }>
  > {
    return this.getByUserAndStation(userId, stationId).pipe(
      map((rows) => this.mapEntrepotRowsToSelect(rows))
    );
  }

  /**
   * Entrepôts document via IDs LC résolus (session / cache sync / sync-status).
   * GET /api/entrepots/entrepot/all?user_id=&station_id= (backend-logiciel-caisse uniquement).
   */
  /**
   * Entrepôts document :
   * 1) localStorage ERP → users_coordination/resolve → LC /entrepot/all
   * 2) sinon gateway ERP (sync + upsert coordination)
   */
  listForDocumentSelectFromLcSession(): Observable<
    Array<{
      id: number;
      nom: string;
      libelle: string;
      ville: string;
      pays: string;
      adresse: string;
      code_entrepot: string | null;
    }>
  > {
    const erp = this.authService.getErpContext();
    if (!erp?.erpUserId || !(erp.erpStationId > 0)) {
      console.warn('[LC Entrepot][listForDocumentSelectFromLcSession] contexte ERP absent');
      return of([]);
    }
    console.log('[LC Entrepot][listForDocumentSelectFromLcSession] ERP localStorage', {
      erpUserId: erp.erpUserId,
      erpStationId: erp.erpStationId,
      code_station: erp.code_station,
    });

    return this.coordinationService.resolve(erp.erpUserId, erp.erpStationId).pipe(
      switchMap((coord) => {
        if (coord) {
          console.log('[LC Entrepot] via users_coordination → LC', coord);
          return this.listForDocumentSelectByUserAndStation(
            coord.user_id_logiciel,
            coord.station_id_logiciel
          ).pipe(
            map((rows) => ({ rows, source: 'users_coordination' as const, lc: coord }))
          );
        }
        console.log('[LC Entrepot] pas de coordination → gateway ERP');
        return this.accesService.listEntrepotsForDocument(erp.code_station, erp.erpStationId).pipe(
          map((res) => ({
            rows: this.mapEntrepotRowsToSelect((res.entrepots || []) as unknown as EntrepotRow[]),
            source: 'erp_gateway' as const,
            lc: res.lc,
          }))
        );
      }),
      map((pack) => {
        console.log('[LC Entrepot][listForDocumentSelectFromLcSession] mapped pour ng-select', {
          source: pack.source,
          lc: pack.lc,
          count: pack.rows.length,
          rows: pack.rows,
        });
        return pack.rows;
      }),
      catchError((err) => {
        console.error('[LC Entrepot][listForDocumentSelectFromLcSession] erreur', err);
        return of([]);
      })
    );
  }

  private mapEntrepotRowsToSelect(rows: EntrepotRow[]) {
    return (rows || [])
      .map((row) => {
        const id = Number(row?.id);
        if (!Number.isFinite(id) || id <= 0) {
          return null;
        }
        const r = row as EntrepotRow & {
          ville?: string | null;
          pays?: string | null;
          code_entrepot?: string | null;
          nom?: string | null;
        };
        const nom = String(r.libelle ?? r.nom ?? '').trim() || `Entrepôt ${id}`;
        const ville = String(r.ville ?? '').trim();
        const pays = String(r.pays ?? '').trim();
        return {
          id,
          nom,
          libelle: nom,
          ville,
          pays,
          adresse: [ville, pays].filter(Boolean).join(' • '),
          code_entrepot: r.code_entrepot != null ? String(r.code_entrepot).trim() : null,
          is_globale:
            r.is_globale === true ||
            Number(r.is_globale) === 1,
        };
      })
      .filter((w): w is NonNullable<typeof w> => w != null);
  }

  /** GET /api/entrepots/entrepot/list/station/:station_id (JWT + station) */
  getEntrepotsErpTableRows(): Observable<EntrepotTableRow[]> {
    let stationId: number;
    try {
      stationId = lcStationId(this.authService);
    } catch (e) {
      return throwError(() => (e instanceof Error ? e : new Error(String(e))));
    }
    const url = `${lcApiBase(this.authService)}/api/entrepots/entrepot/list/station/${stationId}`;
    return this.http.get<EntrepotsErpListResponse>(url).pipe(
      map((res) => {
        if (!res?.success || !Array.isArray(res.items)) {
          throw new Error(res?.error || 'Réponse entrepôts invalide');
        }
        return res.items;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors du chargement des entrepôts.'))
      )
    );
  }

  getEntrepotOptions(): Observable<LogicielCaisseEntrepotOption[]> {
    if (!this.entrepotOptionsCache$) {
      this.entrepotOptionsCache$ = this.getEntrepotsErpTableRows().pipe(
        map((items) =>
          items
            .map((row) => {
              const id = row.id != null ? String(row.id) : '';
              const label =
                row.LIBELLÉ?.trim() ||
                row['CODE ENTREPÔT']?.trim() ||
                (id ? `Entrepôt ${id}` : '');
              return { id, label: label || `Entrepôt ${id}` };
            })
            .filter((o) => o.id !== '')
        ),
        shareReplay(1)
      );
    }
    return this.entrepotOptionsCache$;
  }

  clearEntrepotOptionsCache(): void {
    this.entrepotOptionsCache$ = null;
  }

  /** POST /api/entrepots/entrepot — user_id / station_id depuis le JWT. */
  createEntrepot(body: EntrepotFormPayload): Observable<EntrepotApiRecord> {
    const url = `${lcApiBase(this.authService)}/api/entrepots/entrepot`;
    return this.http.post<EntrepotMutationResponse>(url, body).pipe(
      map((res) => {
        if (!res?.success || res.entrepot == null) {
          throw new Error(res?.error || 'Création impossible');
        }
        this.clearEntrepotOptionsCache();
        const e = res.entrepot as unknown as EntrepotApiRecord;
        if (e.id == null || Number.isNaN(Number(e.id))) {
          throw new Error('Réponse entrepôt invalide (id manquant).');
        }
        return e;
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors de la création.'))
      )
    );
  }

  /** PUT /api/entrepots/entrepot/:id */
  updateEntrepot(id: string | number, body: EntrepotFormPayload): Observable<void> {
    const url = `${lcApiBase(this.authService)}/api/entrepots/entrepot/${encodeURIComponent(String(id))}`;
    return this.http.put<EntrepotMutationResponse>(url, body).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error(res?.error || 'Mise à jour impossible');
        }
        this.clearEntrepotOptionsCache();
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors de la mise à jour.'))
      )
    );
  }

  /** GET /api/entrepots/entrepot-caisses/station/:station_id */
  listStationCaissesForAssociation(): Observable<EntrepotCaisseOption[]> {
    let stationId: number;
    try {
      stationId = lcStationId(this.authService);
    } catch (e) {
      return throwError(() => (e instanceof Error ? e : new Error(String(e))));
    }
    const url = `${lcApiBase(this.authService)}/api/entrepots/entrepot-caisses/station/${stationId}`;
    return this.http.get<{ success: boolean; caisses?: EntrepotCaisseOption[] }>(url).pipe(
      map((res) => {
        if (!res?.success || !Array.isArray(res.caisses)) {
          throw new Error('Réponse caisses invalide');
        }
        return res.caisses.map((c) => ({
          ...c,
          label: this.formatCaisseOptionLabel(c),
        }));
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors du chargement des caisses.'))
      )
    );
  }

  /** PUT /api/entrepots/entrepot/:id/caisses */
  syncEntrepotCaisses(entrepotId: number, caisseIds: number[]): Observable<void> {
    const url = `${lcApiBase(this.authService)}/api/entrepots/entrepot/${entrepotId}/caisses`;
    return this.http.put<{ success: boolean }>(url, { caisse_ids: caisseIds }).pipe(
      map((res) => {
        if (!res?.success) {
          throw new Error('Enregistrement impossible');
        }
      }),
      catchError((err: HttpErrorResponse) =>
        throwError(() => lcHttpError(err, 'Erreur lors de l\'association caisse / entrepôt.'))
      )
    );
  }

  formatCaisseOptionLabel(c: EntrepotCaisseOption): string {
    const num = c.numero_de_caisse != null ? `n° ${c.numero_de_caisse}` : `#${c.id}`;
    const wh = c.entrepot_libelle?.trim() || '';
    const configured = c.has_uuid ? ' — configurée' : ' — à initialiser';
    return wh ? `Caisse ${num} (${wh})${configured}` : `Caisse ${num}${configured}`;
  }
}
